import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, AlertTriangle } from "lucide-react";
import { SiFacebook, SiInstagram, SiTiktok, SiYoutube } from "react-icons/si";

type ConnectablePlatform = "meta" | "tiktok" | "youtube";

const PLATFORM_INFO: Record<ConnectablePlatform, {
  label: string;
  Icon: any;
  description: string;
  scopes: string[];
}> = {
  meta: {
    label: "Facebook + Instagram",
    Icon: SiFacebook,
    description:
      "Connect your Facebook Pages and the Instagram Business accounts linked to them. One sign-in covers both networks.",
    scopes: [
      "Read your list of Pages",
      "Publish posts to selected Pages",
      "Publish posts and reels to linked Instagram Business accounts",
      "Read engagement (likes, comments) on those posts",
    ],
  },
  tiktok: {
    label: "TikTok",
    Icon: SiTiktok,
    description:
      "Connect a TikTok account so we can upload videos directly. Initial publishes go to your inbox as drafts until your account is approved by TikTok for direct posting.",
    scopes: [
      "View your basic profile (name, avatar)",
      "Upload video files",
      "Submit videos for publishing",
    ],
  },
  youtube: {
    label: "YouTube",
    Icon: SiYoutube,
    description:
      "Connect a YouTube channel so we can upload videos directly. Uses the same Google sign-in you already use for Calendar / Drive.",
    scopes: [
      "Read basic channel info",
      "Upload videos to your channel (default visibility: private)",
    ],
  },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPlatform?: ConnectablePlatform | null;
}

export function ConnectAccountDialog({ open, onOpenChange, initialPlatform }: Props) {
  const [platform, setPlatform] = useState<ConnectablePlatform | null>(initialPlatform || null);

  function handleConnect() {
    if (!platform) return;
    // Server route handles state generation + redirect to platform.
    window.location.href = `/api/social/oauth/${platform}/authorize?returnUrl=${encodeURIComponent("/marketing/social?tab=accounts")}`;
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setPlatform(initialPlatform || null); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="dialog-connect-account">
        <DialogHeader>
          <DialogTitle>Connect a platform</DialogTitle>
          <DialogDescription>
            Pick which network you want to link. You'll be sent to the platform to authorize Clarik, then bounced back here.
          </DialogDescription>
        </DialogHeader>

        {!platform ? (
          <div className="grid gap-2">
            {(Object.keys(PLATFORM_INFO) as ConnectablePlatform[]).map((key) => {
              const info = PLATFORM_INFO[key];
              const Icon = info.Icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPlatform(key)}
                  className="flex items-start gap-3 border rounded-md p-3 text-left hover-elevate"
                  data-testid={`button-pick-platform-${key}`}
                >
                  <Icon className="h-5 w-5 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium">{info.label}</div>
                    <div className="text-xs text-muted-foreground">{info.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {(() => {
                const Icon = PLATFORM_INFO[platform].Icon;
                return <Icon className="h-5 w-5" />;
              })()}
              <div className="font-medium">{PLATFORM_INFO[platform].label}</div>
              <Badge variant="outline" className="ml-auto text-xs">OAuth 2.0</Badge>
            </div>

            <div className="text-sm text-muted-foreground">{PLATFORM_INFO[platform].description}</div>

            <div className="space-y-2">
              <div className="text-xs uppercase font-medium tracking-wide text-muted-foreground">
                What you'll be asked to grant
              </div>
              <ul className="text-sm list-disc pl-5 space-y-1">
                {PLATFORM_INFO[platform].scopes.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
            </div>

            <Alert className="border-amber-500/40 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertDescription className="text-amber-900 dark:text-amber-100/80 text-xs">
                Live publishing stays paused until each platform finishes reviewing the Clarik app for your workspace.
                You can connect now and queue drafts; they'll send the moment review clears.
              </AlertDescription>
            </Alert>
          </div>
        )}

        <DialogFooter className="gap-2">
          {platform && (
            <Button variant="ghost" onClick={() => setPlatform(null)} data-testid="button-back-platform">
              Back
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-connect">
            Cancel
          </Button>
          <Button onClick={handleConnect} disabled={!platform} data-testid="button-start-connect">
            <ExternalLink className="h-4 w-4 mr-2" />
            Continue to {platform ? PLATFORM_INFO[platform].label : "platform"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
